import type { MatchRng } from '../match/MatchRng';
import type { MatchState } from '../match/MatchState';
import type { PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { PITCH_GEOMETRY } from '../match/PitchGeometry';
import { clamp, distance, type Vector2 } from '../match/Vector2';

export type GoalkeeperSaveOutcome = 'GOAL' | 'CATCH' | 'PARRY';

/** Goalkeeper-only positioning and ball-control rules. */
export class GoalkeeperSystem {
  positioningTarget(player: PlayerRuntimeState, state: MatchState): Vector2 {
    if (state.ball.state === 'SHOOTING' && state.ball.flight?.teamSide !== player.teamSide) {
      return PITCH_GEOMETRY.clampGoalkeeperPhysicalPosition(player.teamSide, state.ball.targetPosition);
    }
    if (state.ball.state === 'LOOSE' && PITCH_GEOMETRY.isInOwnPenaltyArea(player.teamSide, state.ball.position)) {
      return PITCH_GEOMETRY.clampGoalkeeperPhysicalPosition(player.teamSide, state.ball.position);
    }
    const owner = state.ball.ownerPlayerId
      ? state.players.find((candidate) => candidate.playerId === state.ball.ownerPlayerId)
      : undefined;
    if (owner && owner.teamSide !== player.teamSide
      && PITCH_GEOMETRY.isInOwnPenaltyArea(player.teamSide, owner.position)) {
      return PITCH_GEOMETRY.clampGoalkeeperPhysicalPosition(player.teamSide, owner.position);
    }
    const lateralOffset = clamp((state.ball.position.y - PITCH_GEOMETRY.centre.y) * .18, -8, 8);
    const ballProgress = PITCH_GEOMETRY.relativeProgress(player.teamSide, state.ball.position.x);
    return {
      x: PITCH_GEOMETRY.worldX(player.teamSide, clamp(4.7 + ballProgress * .032, 4.7, 7.9)),
      y: PITCH_GEOMETRY.centre.y + lateralOffset,
    };
  }

  saveScore(goalkeeper: PlayerRuntimeState, target: Vector2, techniquePower: number, rng: MatchRng): number {
    const positioning = Math.max(0, 100 - distance(goalkeeper.position, target) * 5);
    return goalkeeper.stats.defense * .45 + goalkeeper.stats.control * .2 + goalkeeper.stats.power * .15
      + techniquePower * .15 + positioning * .05 - (100 - goalkeeper.staminaCurrent) * .1 + rng.noise(8);
  }

  outcome(goalkeeper: PlayerRuntimeState, shotScore: number, saveScore: number, rng: MatchRng): GoalkeeperSaveOutcome {
    const goalChance = clamp(.04 + (shotScore - saveScore) / 300, .01, .22);
    if (rng.chance(goalChance)) return 'GOAL';
    const catchChance = clamp(.34 + goalkeeper.stats.control * .0045 + (saveScore - shotScore) / 220, .22, .9);
    return rng.chance(catchChance) ? 'CATCH' : 'PARRY';
  }

  claimBonus(player: PlayerRuntimeState, ballPosition: Vector2): number {
    return player.positionType === 'GK' && PITCH_GEOMETRY.isInOwnPenaltyArea(player.teamSide, ballPosition) ? 24 : 0;
  }

  parryVelocity(goalkeeper: PlayerRuntimeState, rng: MatchRng): Vector2 {
    const awayFromGoal = goalkeeper.teamSide === 0 ? 1 : -1;
    return { x: awayFromGoal * rng.between(2.2, 3.8), y: rng.noise(3.2) };
  }
}
